import {
  Eyebrow,
  GradText,
  Icon,
  Section,
  SectionHeading,
} from "../../BatteryOnlyProposal/primitives"
import { formatUsd } from "../../BatteryOnlyProposal/view-helpers"
import type {
  SolarFinanceOption,
  SolarFinanceOptionId,
  SolarPresentationProposal,
} from "../solar-presentation"

const paymentSuffix = (option: SolarFinanceOption): string =>
  option.paymentCadence === `monthly` ? `/mo` : ``

function MoneyStat({
  label,
  value,
  tone = `text`,
}: {
  label: string
  value: string
  tone?: `success` | `text`
}): JSX.Element {
  const color = tone === `success` ? `text-bp-success` : `text-bp-text`
  return (
    <div>
      <Eyebrow className={tone === `success` ? `text-bp-success` : undefined}>
        {label}
      </Eyebrow>
      <div
        className={`font-display mt-1 text-[28px] font-semibold tracking-[-0.02em] tabular-nums ${color}`}
      >
        {value}
      </div>
    </div>
  )
}

export function Financing({
  proposal,
  selected,
  onSelect,
}: {
  proposal: SolarPresentationProposal
  selected: SolarFinanceOption
  onSelect: (id: SolarFinanceOptionId) => void
}): JSX.Element {
  const options = proposal.financeOptions

  return (
    <Section id="financing">
      <SectionHeading eyebrow="How you'll pay">
        {options.length} ways forward. <GradText>Pick what fits</GradText>.
      </SectionHeading>

      <div
        role="radiogroup"
        aria-label="Finance option"
        className="mb-6 grid overflow-hidden rounded-lg border border-bp-border bg-bp-surface sm:grid-cols-2 lg:grid-cols-4"
      >
        {options.map((option) => {
          const isSelected = option.id === selected.id
          return (
            <button
              key={option.id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => onSelect(option.id)}
              className={`flex flex-col gap-2.5 border-t-2 border-r border-r-bp-border px-6 py-5 text-left transition-colors focus-visible:ring-2 focus-visible:ring-bp-accent focus-visible:outline-none focus-visible:ring-inset ${
                isSelected
                  ? `border-t-bp-accent bg-bp-elevated-2`
                  : `border-t-transparent hover:bg-bp-glass-hi`
              }`}
            >
              <span className="font-display text-[13px] font-semibold text-bp-text">
                {option.partnerName}
              </span>
              <span className="text-xs text-bp-text-muted">
                {option.productName}
              </span>
              <span className="mt-auto flex items-baseline gap-1">
                <span
                  className={`font-display text-[22px] font-semibold tracking-[-0.02em] tabular-nums ${
                    isSelected ? `text-bp-accent` : `text-bp-text`
                  }`}
                >
                  {formatUsd(option.payment)}
                </span>
                <span className="text-xs text-bp-text-muted">
                  {paymentSuffix(option)}
                </span>
              </span>
            </button>
          )
        })}
      </div>

      <div className="grid gap-3.5 lg:grid-cols-[1.4fr_1fr]">
        <div className="relative overflow-hidden rounded-lg border border-bp-border bg-bp-surface p-6 lg:p-8">
          <div className="pointer-events-none absolute inset-0 bg-[image:radial-gradient(ellipse_50%_80%_at_100%_0%,var(--bp-accent-soft),transparent_60%)]" />
          <div className="relative">
            <Eyebrow>Selected · {selected.partnerName}</Eyebrow>
            <h3 className="mt-1 mb-4 text-[length:var(--step-2)] font-semibold tracking-[-0.02em] text-bp-text">
              {selected.productName}
            </h3>
            <p className="mb-[22px] text-[length:var(--step--1)] text-bp-text-muted">
              {selected.terms}
            </p>
            <div className="grid grid-cols-1 gap-[18px] sm:grid-cols-3">
              <MoneyStat
                label="Payment"
                value={`${formatUsd(selected.payment)}${paymentSuffix(selected)}`}
              />
              <MoneyStat
                label="Total cost"
                value={formatUsd(selected.totalCost)}
              />
              <MoneyStat
                label="You save"
                value={formatUsd(selected.lifetimeSavings)}
                tone="success"
              />
            </div>
            <hr className="my-6 border-bp-border" />
            <ul className="flex flex-col gap-2.5">
              {selected.highlights.map((highlight) => (
                <li
                  key={highlight}
                  className="flex items-center gap-2.5 text-[13.5px] text-bp-text-soft"
                >
                  <span className="grid size-[18px] place-items-center rounded-full bg-bp-success-soft text-bp-success">
                    <Icon name="check" className="size-2.5" />
                  </span>
                  {highlight}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="rounded-lg border border-bp-border bg-bp-elevated-2 p-7">
          <Eyebrow className="mb-4">Total cost worksheet</Eyebrow>
          <dl>
            {proposal.pricing.map((line, index) => (
              <div
                key={line.label}
                className={`flex items-baseline justify-between py-[11px] ${
                  index < proposal.pricing.length - 1
                    ? `border-b border-dashed border-bp-border`
                    : ``
                }`}
              >
                <dt className="text-[13px] text-bp-text-muted">{line.label}</dt>
                <dd
                  className={`font-display text-[15px] font-semibold tabular-nums ${
                    line.credit ? `text-bp-success` : `text-bp-text`
                  }`}
                >
                  {formatUsd(line.credit ? -line.amount : line.amount)}
                </dd>
              </div>
            ))}
          </dl>
          <div className="mt-4 flex items-baseline justify-between rounded-md border border-bp-border-strong bg-bp-page px-[18px] py-4">
            <div>
              <div className="text-[11px] font-semibold tracking-[0.06em] text-bp-text-muted uppercase">
                Net to homeowner
              </div>
              <div className="mt-[3px] text-[11px] text-bp-text-dim">
                after ITC, before financing interest
              </div>
            </div>
            <span className="font-display text-[28px] font-bold tracking-[-0.02em] text-bp-success tabular-nums">
              {formatUsd(proposal.netCost)}
            </span>
          </div>
        </div>
      </div>
    </Section>
  )
}
